import type { OrganArtworkQuality } from '#assets/images/gutBodyConnections';
import { toImageSrc } from '#helpers/imageSrc';
import {
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsOrgan,
  OrganLookupCode,
} from '@repo/types';
import c from 'classnames';
import { Fragment } from 'react';
import { Spinfinity } from '../Spinfinity';
import Dot, { type GutBodyConnectionsHoverVariant } from './Dot';
import styles from './GutBodyConnectionsBody.module.css';
import Organ from './Organ';
import { useGutBodyConnectionsImages } from './hooks';

export { getBodyAgeGroup } from '#assets/images/gutBodyConnections';
export * from './copy';
export * from './systems';

type GutBodyConnectionsBodyProps = {
  organs: GutBodyConnectionsOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  /** Omit to render a static body, as the PDF report does. */
  onOrganSelect?: (organ: GutBodyConnectionsOrgan) => void;
  /** PDF renders eagerly: paged.js would capture whichever frame it caught. */
  waitForImages?: boolean;
  /** In case we want to hide the tooltips, example: PDFs */
  showDotAndTooltips?: boolean;
  /** In case we want to show the gradient fade in the body */
  fadeBody?: boolean;
  /** Lands on the container. */
  className?: string;
  /** `pill` names the organ only, for a surface that lists its flags already. */
  hoverVariant?: GutBodyConnectionsHoverVariant;
  /** `low` draws the downscaled organs, for the PDF report. */
  quality?: OrganArtworkQuality;
  /** Codes to keep at full opacity; every other organ stays dimmed. */
  litOrgans?: OrganLookupCode[];
  /** Pulse every dimmed (non-lit) organ between its resting opacity and full. */
  pulseUnlit?: boolean;
  /** Turn off organ hover (no hover lighting, mirror reveal, or label reveal). */
  disableHover?: boolean;
  /** Draw this colour's organ artwork instead of the flag-level colours. */
  colorVariant?: GutBodyConnectionsColorVariant;
};

export function GutBodyConnectionsBody({
  organs,
  age,
  pregnant,
  sex,
  onOrganSelect,
  waitForImages,
  showDotAndTooltips = true,
  fadeBody,
  className,
  hoverVariant,
  quality,
  litOrgans,
  pulseUnlit,
  disableHover,
  colorVariant,
}: GutBodyConnectionsBodyProps) {
  const { body, bodyAgeGroup, systems, imgProps, ready } =
    useGutBodyConnectionsImages({
      organs,
      age,
      pregnant,
      sex,
      waitForImages,
      quality,
      colorVariant,
    });

  return (
    <div
      className={c(
        styles.container,
        className,
        pulseUnlit && styles.pulseUnlit,
        disableHover && styles.noHover,
        !ready && styles.pending,
      )}
    >
      <img
        src={toImageSrc(body)}
        className={c(styles.bodySilhouette, fadeBody && styles.fadeBody)}
        {...imgProps('bodySilhouette')}
      />
      {systems.map(({ organ, parts }) => (
        <div key={organ.id} className={styles.system}>
          {parts.map(({ part, src }) => {
            const placement = c(
              styles.organ,
              styles[part],
              styles[bodyAgeGroup],
            );

            return (
              <Fragment key={part}>
                <Organ
                  className={c(
                    placement,
                    litOrgans?.includes(organ.lookup_code) && styles.lit,
                  )}
                  ariaLabel={organ.display_name}
                  onSelect={
                    onOrganSelect ? () => onOrganSelect(organ) : undefined
                  }
                >
                  <img
                    src={toImageSrc(src)}
                    className={styles.organImage}
                    {...imgProps(part)}
                  />
                </Organ>
                {/* An inert twin of the organ, so its tag paints above every organ layer. */}
                {showDotAndTooltips && (
                  <span className={c(placement, styles.tag)} aria-hidden>
                    <img
                      src={toImageSrc(src)}
                      className={c(styles.organImage, styles.tagSizer)}
                      alt=''
                    />
                    <Dot
                      displayName={organ.display_name}
                      hoverVariant={hoverVariant}
                    />
                  </span>
                )}
              </Fragment>
            );
          })}
        </div>
      ))}
      {!ready && (
        <span className={styles.loader}>
          <Spinfinity />
        </span>
      )}
    </div>
  );
}
